import Vue from "vue";
import { CrudService } from "../core/services/crudService";
import { Settings } from "../core/services/configService";
import { CustomerHelper } from "../crm/helpers/customerHelper";
import { GuidelineApi, CardGuideline, DayDone } from "../components/guidelines/guidelineApi";
import { TreatmentApi, AsNeededSlot } from "../components/treatments/treatmentApi";

const InProgress = 2;
const LessonCompleted = 2;

export interface TodayData {
  today: string;
  rows: any[];
  bookings: any[];
  surveys: any[];
  programs: any[];
  programByGuideline: { [id: number]: string };
  treatmentRows: any[];
  treatmentUnscheduled: any[];
  treatmentDay: string;
  treatmentFailed: boolean;
  hasTreatment: boolean;
  treatmentLoads: number;
  courses: any[];
}

// Everything of the day (D41): today's appointments, pending questionnaires, the guidelines and the treatment takes to mark,
// plus the programs and courses in progress.
export class TodayLoader {
  static empty(): TodayData {
    return { today: "", rows: [], bookings: [], surveys: [], programs: [], programByGuideline: {}, treatmentRows: [], treatmentUnscheduled: [], treatmentDay: "", treatmentFailed: false, hasTreatment: false, treatmentLoads: 0, courses: [] };
  }

  static async load(): Promise<TodayData> {
    const customer = CustomerHelper.getStoredCustomer();
    const out: TodayData = TodayLoader.empty();
    if (!customer) return out;
    const crud = new CrudService();
    const safe = (call: Promise<any>) => call.catch(() => null);
    const [guidelines, bookings, surveys, programs, treatment, courses, treatmentDetail] = await Promise.all([
      safe(GuidelineApi.list()),
      safe(crud.httpGet(`${Settings.HostName}/api/publicbookings/customer-bookings/${customer}/today`)),
      safe(crud.httpGet(`${Settings.HostName}/api/publiccustomersurveys/customer-surveys/${customer}`)),
      safe(crud.httpGet(`${Settings.HostName}/api/publicprogrampacks/customer/${customer}`)),
      safe(TreatmentApi.today()),
      safe(crud.httpGet(`${Settings.HostName}/api/public-courses/my-courses`)),
      safe(TreatmentApi.detail()),
    ]);
    if (courses && courses.ok) {
      out.courses = await TodayLoader.coursesInProgress(crud, courses.data);
    }
    if (guidelines && guidelines.ok) {
      out.today = GuidelineApi.day(guidelines.today);
      out.rows = guidelines.data;
    }
    if (bookings && bookings.ok) {
      out.bookings = bookings.data;
    }
    if (Array.isArray(surveys)) {
      out.surveys = surveys.filter((s: any) => s.answeredDate === null && s.token);
    }
    if (programs && programs.ok) {
      out.programs = programs.data.filter((e: any) => e.status === InProgress);
      programs.data.forEach((e: any) => e.columns.forEach((c: any) => c.cards
        .filter((k: any) => k.cardType === CardGuideline && k.customerGuidelineId)
        .forEach((k: any) => Vue.set(out.programByGuideline, k.customerGuidelineId, e.name))));
    }
    // Answers on both sides of midnight are never mixed: a treatment of another day shows as failed and its retry reloads all.
    out.treatmentFailed = !treatment || treatment.ok !== true || (!!out.today && GuidelineApi.day(treatment.model.today) !== out.today);
    if (!out.treatmentFailed) {
      out.treatmentRows = treatment.model.rows;
      out.treatmentDay = GuidelineApi.day(treatment.model.today);
      out.hasTreatment = out.treatmentRows.length > 0 || treatment.model.hasTreatment === true;
      if (!out.today) out.today = GuidelineApi.day(treatment.model.today);
    }
    if (treatmentDetail && treatmentDetail.ok) {
      out.treatmentUnscheduled = TreatmentApi.unscheduled(treatmentDetail.model.lines);
    }
    return out;
  }

  // The courses with lessons left, each with the first lesson not completed (the same the course page offers to continue).
  static async coursesInProgress(crud: CrudService, courses: any[]): Promise<any[]> {
    const pending = (courses || []).filter((c: any) => c.lessonCount > c.completedLessons);
    const details = await Promise.all(pending.map((c: any) => crud.httpGet(`${Settings.HostName}/api/public-courses/${c.id}`).catch(() => null)));
    return pending.map((c: any, i: number) => {
      const d = details[i];
      const lessons = d && d.ok ? d.sections.flatMap((x: any) => x.lessons) : [];
      const next = lessons.find((l: any) => !(d.progress || []).some((x: any) => x.courseLessonId === l.id && x.status === LessonCompleted)) || null;
      return { ...c, nextLesson: next };
    });
  }

  // False when the day changed since the page loaded: the caller reloads everything, never mixing two days.
  // A failed reload hides the old rows: nothing stale stays markable. Only the latest reload is shown.
  static async reloadTreatment(data: TodayData): Promise<boolean> {
    const load = ++data.treatmentLoads;
    const treatment = await TreatmentApi.today().catch(() => null);
    if (load !== data.treatmentLoads) return true;
    data.treatmentFailed = !treatment || treatment.ok !== true;
    if (data.treatmentFailed) {
      data.treatmentRows = [];
      return true;
    }
    const day = GuidelineApi.day(treatment.model.today);
    const shown = data.treatmentDay || data.today;
    if (shown && day !== shown) return false;
    data.treatmentRows = treatment.model.rows;
    data.treatmentDay = day;
    data.hasTreatment = data.treatmentRows.length > 0 || treatment.model.hasTreatment === true;
    return true;
  }

  static treatmentDue(data: TodayData): any[] {
    return data.treatmentRows.filter((r: any) => r.slot !== AsNeededSlot);
  }

  // The ring measures only what the patient marks today: guidelines still running and the takes with a time.
  static count(data: TodayData): number {
    return data.rows.filter((r: any) => !r.ended).length + this.treatmentDue(data).length;
  }

  static done(data: TodayData): number {
    return data.rows.filter((r: any) => !r.ended && r.todayStatus === DayDone).length
      + this.treatmentDue(data).filter((r: any) => r.items.every((i: any) => i.taken)).length;
  }

  // Anything at all for today, appointments and questionnaires included.
  static planned(data: TodayData): number {
    return this.count(data) + data.bookings.length + data.surveys.length;
  }
}
